"use client";

import { useState } from "react";
import Link from "next/link";
import { Mail, Send, MailCheck, Loader2, AlertTriangle } from "lucide-react";
import { AuthLayout } from "@/components/layout";
import { Button, Input } from "@/components/ui";
import { authService } from "@/services/authService";
import { handleApiError } from "@/lib/errorHandler";

const MONO_LABEL = "text-[13px] font-medium tracking-[-0.01em] text-muted";

export default function ForgotPasswordPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [requestError, setRequestError] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validateEmail = () => {
    if (!email) {
      setError("E-posta adresi gereklidir");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Geçerli bir e-posta adresi girin");
      return false;
    }
    setError("");
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setRequestError("");
    if (!validateEmail()) return;

    setIsLoading(true);
    try {
      await authService.forgotPassword({ email });
      setIsSubmitted(true);
    } catch (error) {
      console.error("Password reset error:", error);
      setRequestError(handleApiError(error));
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <AuthLayout brand={{ tagline: "Kapıda isim, içeride kontrol." }}>
        <div className="flex flex-col items-start">
          <span className="mb-[22px] inline-flex h-14 w-14 items-center justify-center rounded-full border border-success-glow-20 bg-success-glow-12 text-success-light">
            <MailCheck
              className="h-[26px] w-[26px]"
              strokeWidth={1.7}
              aria-hidden
            />
          </span>

          <h1 className="font-display text-[30px] font-bold leading-[1.2] tracking-[-0.024em] text-foreground">
            E-posta gönderildi
          </h1>

          <p className="mb-[26px] mt-3 max-w-[340px] text-sm leading-[1.6] text-muted-foreground">
            <span className="break-all font-mono text-[13.5px] font-medium tracking-[-0.01em] text-foreground">
              {email}
            </span>{" "}
            adresine bir sıfırlama bağlantısı gittiyse birkaç dakika içinde
            ulaşır.
          </p>

          <div className="flex w-full flex-col gap-3.5">
            <Button
              type="button"
              variant="secondary"
              size="lg"
              className="w-full border-border bg-card text-foreground hover:border-brand hover:bg-card hover:text-brand-light"
              onClick={() => {
                setIsSubmitted(false);
                setEmail("");
                setError("");
                setRequestError("");
              }}
            >
              Farklı bir e-posta dene
            </Button>

            <Link
              href="/login"
              className="text-[13px] tracking-[-0.005em] text-muted transition-colors duration-150 hover:text-brand-light"
            >
              Giriş sayfasına dön
            </Link>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Şifremi Unuttum"
      description="E-posta adresinizi girin, size şifre sıfırlama bağlantısı gönderelim."
      brand={{ tagline: "Kapıda isim, içeride kontrol." }}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {requestError && (
          <div className="flex items-center gap-2.5 rounded-[10px] border border-error-glow-20 bg-error-glow-12 px-3.5 py-3">
            <AlertTriangle
              className="h-4 w-4 shrink-0 text-error-light"
              aria-hidden
            />
            <span className="text-[13px] font-medium tracking-[-0.005em] text-error-light">
              {requestError}
            </span>
          </div>
        )}

        <Input
          id="email"
          label="E-posta"
          labelClassName={MONO_LABEL}
          type="email"
          placeholder="ornek@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={error}
          leftIcon={Mail}
          autoComplete="email"
          disabled={isLoading}
        />

        <Button
          type="submit"
          size="lg"
          className="mt-2 w-full disabled:cursor-default disabled:opacity-100"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Gönderiliyor...
            </>
          ) : (
            <>
              <Send className="h-4 w-4 shrink-0" aria-hidden />
              Bağlantı Gönder
            </>
          )}
        </Button>

        <div className="flex justify-center pt-1">
          <Link
            href="/login"
            className="inline-flex items-center gap-1.5 text-[13px] tracking-[-0.005em] text-muted transition-colors duration-150 hover:text-brand-light"
          >
            <span className="text-[15px] leading-none">‹</span>
            Giriş sayfasına dön
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}
